// src/shared/components/dashboard/MiniBar.tsx
// ────────────────────────────────────────────────────────────────────────────
// Horizontal bar chart — single source of truth for namespace breakdowns,
// distribution visualizations, and categorical bar displays.
//
// Unifies: ArchiverDashboard.MiniBar, ArchiverCard namespace bars.
// ────────────────────────────────────────────────────────────────────────────
import React, { memo, useMemo } from 'react';

export interface MiniBarItem {
  label: string;
  value: number;
  /** CSS color (hex or Tailwind class). If starts with 'bg-', applied as className */
  color: string;
}

export interface MiniBarProps {
  items: MiniBarItem[];
  /** Label column width — default: 'w-28' */
  labelWidth?: string;
  /** Bar height — default: 'h-3' */
  barHeight?: string;
}

function MiniBar({ items, labelWidth = 'w-28', barHeight = 'h-3' }: MiniBarProps) {
  const max = useMemo(() => Math.max(...items.map(i => i.value), 1), [items]);

  return (
    <div className="flex flex-col gap-1.5">
      {items.map(item => {
        const isTwClass = item.color.startsWith('bg-');
        return (
          <div key={item.label} className="flex items-center gap-2 text-xs">
            <span className={`${labelWidth} truncate text-neutral-400 font-mono text-[11px]`}>
              {item.label}
            </span>
            <div className={`flex-1 ${barHeight} rounded-full bg-white/[0.04] overflow-hidden`}>
              <div
                className={`h-full rounded-full transition-all duration-300 ${isTwClass ? item.color : ''}`}
                style={{
                  width: `${Math.max((item.value / max) * 100, 1)}%`,
                  ...(isTwClass ? {} : { backgroundColor: item.color }),
                }}
              />
            </div>
            <span className="w-14 text-right text-neutral-300 tabular-nums text-[11px]">
              {item.value.toLocaleString()}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default memo(MiniBar);
